import { Dropdown, Menu, Space, Table, Tag } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { api } from "../../config/api";
import DashboardLayout from "../layout";
import Header from "../header/Header";
import { useMyContext } from "../../context/MyContext";
import Loader from "../shared/loader/loader";
import { useState } from "react";

const SupportTickets = () => {
  const navigate = useNavigate();
  const { notifications, user, token, isAuthenticated } = useMyContext();
  const queryClient = useQueryClient();
  const [showMoreIdx, setShowMoreIdx] = useState(null);
  const { data, isLoading, error } = useQuery(
    ["tickets"],
    async () => {
      if (!token) {
        throw new Error("No token available");
      }
      try {
        const res = await api.get(`/tickets`, {
          headers: { authorization: `Bearer ${token}` }
        });
        const sortedData = res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return sortedData;
      } catch (error) {
        if (error?.response?.status === 401 && error?.response?.data?.error === 'You are not authorized to make this request') {
          window.location.reload();
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!token && isAuthenticated,
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error("Error fetching tickets:", error);
      },
    }
  );

  const closeTicketMutation = useMutation(
    async (ticketId) => {
      const currentTicket = data?.find(t => t._id === ticketId);
      const newStatus = !currentTicket?.status;

      await api.put(`/tickets/${ticketId}/status`, { status: newStatus }, {
        headers: { authorization: `Bearer ${token}` }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tickets"]);
      },
    }
  );

  const handleChatClick = async (record) => {
    try {
      await api.put(`/notifications/ticket-chat/${user?.uid}`, {
        role: user?.role,
        updateAllNotifications: true
      }, {
        headers: { authorization: `Bearer ${token}` }
      });
      navigate(`/tickets/${record?._id}`);
    } catch (error) {
      console.log("error", error);
    }
  };

  if (!isAuthenticated) {
    return <Loader />;
  }

  if (error) {
    return <div>Error loading tickets. Please try again later.</div>;
  }

  const getStatusClasses = (status) => {
    switch (status) {
      case true:
        return 'bg-green-600 ';
      case false:
        return 'bg-red-700';
      default:
        return 'bg-gray-800';
    }
  }
  const getPriorityClasses = (priority) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'bg-violet-800 ';
      case 'high':
        return 'bg-red-700';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-sky-500';
      default:
        return 'bg-gray-800';
    }
  }

  // console.log(data)
  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (_, record) => dayjs(record?.createdAt).format("DD MMM YYYY"),
    },
    {
      title: "Subject",
      dataIndex: "description",
      render: (_, record) => {
        const description = record?.description || '';
        const isLong = description.length > 50;
        const shortDesc = description.slice(0, 50);

        const isExpanded = showMoreIdx === record._id;

        return (
          <div className="">
            <span>
              {isExpanded ? description : (isLong ? `${shortDesc}...` : description)}
            </span>
            {isLong && (
              <button
                type="button"
                onClick={() => setShowMoreIdx(isExpanded ? null : record._id)}
                className="text-blue-600 text-sm font-medium hover:underline mt-1 ml-1"
              >
                {isExpanded ? 'Read less' : 'Read more'}
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: "Quote / Order No.",
      dataIndex: "quoteNumber",
      render: (_, record) => (record?.quoteNumber || record?.orderNumber || "-"),
    },
    {
      title: "Type",
      dataIndex: "type",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "left",
      render: (_, record) => (
        <div className="flex items-center">
          <span className={`rounded text-white px-2 py-1 font-medium inline-block ${getStatusClasses(record?.status)}`}>
            {record?.status ? "Open" : "Closed"}
          </span>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      align: "left",
      render: (_, record) => (
        <div className="flex items-center">
          <span className={`rounded text-white px-2 py-1 font-medium inline-block ${getPriorityClasses(record?.priority)}`}>
            {record?.priority}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      render: (_, record, index) => (
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => handleChatClick(record)}
                key="view ticket"
              >
                <Space className="relative">
                  <span>Chat</span>
                  {notifications?.find(
                    (e) => e?.refId === record?._id && e?.read === false
                  ) ? (
                    <div className="w-2 h-2 absolute -top-[2px] right-0 bg-red-600 rounded-full"></div>
                  ) : null}
                </Space>
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  closeTicketMutation.mutate(record?._id)
                }
                key="close ticket"
              >
                <Space>
                  {record?.status ? "Close" : "Reopen"} Ticket
                </Space>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Space className="relative">
            <button className="border relative p-1 rounded-md border-[#053F92]">
              <PiDotsThreeVerticalBold size={20} />
              {notifications?.find(
                (e) => e?.refId === record?._id && e?.read === false
              ) ? (
                <div className="w-2 h-2 absolute -top-[2px] right-0 bg-red-600 rounded-full"></div>
              ) : null}
            </button>
          </Space>
        </Dropdown>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Header title="Support and Help" />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex flex-col p-5">
          {data?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tickets found
            </div>
          ) : (
            <Table columns={columns} dataSource={data} loading={isLoading} />
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default SupportTickets;
